/*
 * This class is copied verbatim from Backstage 1.24.0.
 */

import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import { TextFieldProps } from '@material-ui/core/TextField/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { ChangeEvent, ComponentProps, Fragment, ReactNode } from 'react';
import { Controller, FieldErrors } from 'react-hook-form';

/**
 * Props for {@link AutocompleteTextField}.
 *
 * @public
 */
export interface AutocompleteTextFieldProps<TFieldValue extends string> {
  name: TFieldValue;
  options: string[];
  required?: boolean;

  errors?: FieldErrors;
  rules?: ComponentProps<typeof Controller>['rules'];

  loading?: boolean;
  loadingText?: string;

  helperText?: ReactNode;
  errorHelperText?: string;

  textFieldProps?: Omit<TextFieldProps, 'required' | 'fullWidth'>;
}

/**
 * An autocompletion text field for the catalog import flows.
 *
 * @public
 */
export const AutocompleteTextField = <TFieldValue extends string>(
  props: AutocompleteTextFieldProps<TFieldValue>,
) => {
  const {
    name,
    options,
    required,
    errors,
    rules,
    loading = false,
    loadingText,
    helperText,
    errorHelperText,
    textFieldProps = {},
  } = props;

  return (
    <Controller
      name={name}
      rules={rules}
      render={({ field: { onChange } }) => (
        <Autocomplete
          loading={loading}
          loadingText={loadingText}
          options={options || []}
          autoSelect
          freeSolo
          onChange={(_event: ChangeEvent<{}>, value: string | null) =>
            onChange(value)
          }
          renderInput={params => (
            <TextField
              {...params}
              helperText={(errors?.[name] && errorHelperText) || helperText}
              error={Boolean(errors?.[name])}
              margin="normal"
              variant="outlined"
              required={required}
              inputProps={{
                'aria-label': 'select option',
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size="1em" />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </Fragment>
                ),
              }}
              {...textFieldProps}
            />
          )}
        />
      )}
    />
  );
};
